/**
 * Relevé mensuel pour la mesure de rentabilité (27/10/2026), et le même par
 * semaine du mercredi au mardi (la semaine du magasin se termine le mardi).
 *
 * CA HT de tous les canaux, coût de la marchandise vendue et démarque, mois par
 * mois. Les charges fixes (personnel, frais généraux, amortissements) vivent
 * dans FOLIES-Compta, sur le Mac mini : on n'en duplique rien ici. Le bouton
 * « Copier pour la compta » met le relevé dans le presse-papiers, au format
 * que l'onglet Rentabilité de FOLIES-Compta importe tel quel.
 */
import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface Mois {
  month: string;
  /** Vue par semaine : nom de la semaine = son mardi */
  week?: string;
  du?: string;
  au?: string;
  en_cours?: boolean;
  ca_ht: number;
  cout_marchandise_vendue: number;
  demarque: number;
  /** Consommation personnelle, échantillons, repas du personnel, sponsoring — hors démarque */
  prelevements?: number;
  heures: null;
  detail: {
    caisse_ht: number;
    eshop_ht: number;
    b2b_ht: number;
    jours_z: number;
    jours_ouverts_sans_z: number;
    /** Dates des jours de vente sans Z (AAAA-MM-JJ) */
    jours_sans_z?: string[];
    couverture_cout: number | null;
    /** Boutique en ligne selon le client, noté à la préparation (28/10/2026) */
    eshop_clients?: { nouveaux_ht: number; magasin_ht: number; non_renseigne_ht: number; nouveaux_nombre: number };
    /** Part de l'épicerie sèche dans les paniers en ligne (0-1), null sans vente */
    part_epicerie_eshop?: number | null;
    /** Stock d'épicerie sèche au prix d'achat, à la fin de la période */
    stock_epicerie?: number;
  };
}

const eur = (v: number) => `${v.toLocaleString('fr-BE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const moisCourant = () => new Date().toISOString().slice(0, 7);
const jourCourt = (iso: string) => {
  const [y, mo, d] = iso.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' });
};
const libelleMois = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
};

export default function RentabiliteMensuellePage() {
  const [vue, setVue] = useState<'mois' | 'semaine'>('mois');
  const [nbSemaines, setNbSemaines] = useState(12);
  const [du, setDu] = useState(() => `${moisCourant().slice(0, 4)}-01`);
  const [au, setAu] = useState(moisCourant);
  const [mois, setMois] = useState<Mois[] | null>(null);
  // Vue des lignes affichées : au clic sur « Par semaine », les lignes du mois
  // restent à l'écran jusqu'à l'arrivée des semaines — sans `du`/`au`.
  const [vueLignes, setVueLignes] = useState<'mois' | 'semaine'>('mois');
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [copie, setCopie] = useState('');

  useEffect(() => {
    if (vue === 'mois' && (!du || !au || du > au)) return;
    let annule = false;
    setChargement(true);
    setErreur('');
    setCopie('');
    const demande =
      vue === 'mois'
        ? api.get<{ months: Mois[] }>(`/analytics/rentabilite-mensuelle?from=${du}&to=${au}`).then((r) => r.months)
        : api
            .get<{ weeks: Mois[] }>(`/analytics/rentabilite-hebdo?semaines=${nbSemaines}`)
            .then((r) => r.weeks.map((w) => ({ ...w, month: w.week as string })));
    demande
      .then((lignes) => {
        if (annule) return;
        setMois(lignes);
        setVueLignes(vue);
      })
      .catch((e: any) => !annule && setErreur(e?.response?.data?.message || e?.message || 'Relevé illisible'))
      .finally(() => !annule && setChargement(false));
    return () => {
      annule = true;
    };
  }, [vue, du, au, nbSemaines]);

  const copier = async () => {
    if (!mois) return;
    const texte = JSON.stringify({ months: mois });
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(`${mois.length} mois copiés : collez-les dans FOLIES-Compta › Rentabilité › Importer.`);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé) : on propose le fichier
      const url = URL.createObjectURL(new Blob([texte], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `releve-mensuel-${du}-${au}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setCopie('Presse-papiers indisponible : le relevé a été téléchargé.');
    }
  };

  const total = (f: (m: Mois) => number) => (mois || []).reduce((s, m) => s + f(m), 0);
  const sansZ = total((m) => m.detail.jours_ouverts_sans_z);
  const datesSansZ = (mois || []).flatMap((m) => m.detail.jours_sans_z || []);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{vue === 'mois' ? 'Relevé mensuel' : 'Relevé par semaine'}</h1>
          <p className="mt-2 text-gray-600">
            Ventes HT de tous les canaux, coût de ce qui a été vendu et démarque. Les charges fixes sont dans FOLIES-Compta.
            {vue === 'semaine' && ' Une semaine court du mercredi au mardi.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Période">
            {(['mois', 'semaine'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVue(v)}
                aria-pressed={vue === v}
                className={`min-h-[44px] px-4 font-medium ${vue === v ? 'bg-green-700 text-white' : 'bg-white text-gray-700'}`}
              >
                {v === 'mois' ? 'Par mois' : 'Par semaine'}
              </button>
            ))}
          </div>
          {vue === 'mois' ? (
            <>
              <input type="month" value={du} onChange={(e) => e.target.value && setDu(e.target.value)} className="min-h-[44px] rounded-lg border border-gray-300 px-3" aria-label="Du" />
              <span className="text-gray-500">→</span>
              <input type="month" value={au} onChange={(e) => e.target.value && setAu(e.target.value)} className="min-h-[44px] rounded-lg border border-gray-300 px-3" aria-label="Au" />
            </>
          ) : (
            <select
              value={nbSemaines}
              onChange={(e) => setNbSemaines(Number(e.target.value))}
              className="min-h-[44px] rounded-lg border border-gray-300 px-3"
              aria-label="Nombre de semaines"
            >
              {[4, 8, 12, 26, 52].map((n) => (
                <option key={n} value={n}>{n} dernières semaines</option>
              ))}
            </select>
          )}
          {vue === 'mois' && <button
            onClick={copier}
            disabled={!mois || chargement}
            className="min-h-[44px] px-5 rounded-lg bg-green-700 text-white font-medium disabled:opacity-50"
          >
            📋 Copier pour la compta
          </button>}
        </div>
      </div>

      {copie && <p className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-green-800">{copie}</p>}
      {erreur && <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-800">{erreur}</p>}
      {vue === 'mois' && du > au && <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-800">Le mois de début est après le mois de fin.</p>}
      {sansZ > 0 && (
        <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-800">
          {sansZ} jour(s) de vente sans rapport Z
          {datesSansZ.length > 0 && <> ({datesSansZ.map(jourCourt).join(', ')})</>} : leur caisse n'est pas comptée. Clôturez ces journées pour un CA complet.
        </p>
      )}
      {chargement && <p className="text-gray-500">Calcul…</p>}

      {mois && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-2">{vueLignes === 'mois' ? 'Mois' : 'Semaine'}</th>
                <th className="px-3 py-2 text-right">Caisse (Z)</th>
                <th className="px-3 py-2 text-right">Boutique en ligne</th>
                <th className="px-3 py-2 text-right">B2B</th>
                <th className="px-3 py-2 text-right">CA HT total</th>
                <th className="px-3 py-2 text-right">Coût vendu</th>
                <th className="px-3 py-2 text-right">Démarque</th>
                <th className="px-3 py-2 text-right">Marge</th>
                <th className="px-3 py-2 text-right" title="Consommation personnelle, échantillons, repas du personnel, sponsoring — au coût d'achat, hors démarque">Conso perso</th>
                <th className="px-3 py-2 text-right" title="Part du CA caisse et B2B dont le prix d'achat est connu">Coût connu</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 tabular-nums">
              {mois.map((m, i) => {
                const marge = m.ca_ht - m.cout_marchandise_vendue - m.demarque;
                const precedente = vueLignes === 'semaine' && i > 0 ? mois[i - 1] : null;
                const evolution = precedente && precedente.ca_ht > 0 && !m.en_cours ? Math.round((100 * (m.ca_ht - precedente.ca_ht)) / precedente.ca_ht) : null;
                return (
                  <tr key={m.month}>
                    <td className={`px-3 py-2 ${vueLignes === 'mois' ? 'capitalize' : ''}`}>
                      {vueLignes === 'semaine' && m.du && m.au ? `${jourCourt(m.du)} → ${jourCourt(m.au)}` : libelleMois(m.month)}
                      {m.en_cours && <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">en cours</span>}
                      {m.detail.jours_ouverts_sans_z > 0 && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          sans Z : {(m.detail.jours_sans_z || []).length > 0 ? (m.detail.jours_sans_z || []).map(jourCourt).join(', ') : `${m.detail.jours_ouverts_sans_z} j`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{eur(m.detail.caisse_ht)}</td>
                    <td className="px-3 py-2 text-right">{eur(m.detail.eshop_ht)}</td>
                    <td className="px-3 py-2 text-right">{eur(m.detail.b2b_ht)}</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {eur(m.ca_ht)}
                      {evolution !== null && (
                        <span className={`ml-1 text-xs font-normal ${evolution >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {evolution >= 0 ? '+' : ''}{evolution} %
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{eur(m.cout_marchandise_vendue)}</td>
                    <td className="px-3 py-2 text-right">{eur(m.demarque)}</td>
                    <td className="px-3 py-2 text-right">
                      {eur(marge)}
                      {m.ca_ht > 0 && <span className="ml-1 text-gray-500">({Math.round((100 * marge) / m.ca_ht)} %)</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{eur(m.prelevements ?? 0)}</td>
                    <td className="px-3 py-2 text-right">{m.detail.couverture_cout === null ? '—' : `${Math.round(m.detail.couverture_cout * 100)} %`}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-bold tabular-nums">
              <tr>
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.detail.caisse_ht))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.detail.eshop_ht))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.detail.b2b_ht))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.ca_ht))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.cout_marchandise_vendue))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.demarque))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.ca_ht - m.cout_marchandise_vendue - m.demarque))}</td>
                <td className="px-3 py-2 text-right">{eur(total((m) => m.prelevements ?? 0))}</td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
          <p className="px-3 py-2 text-xs text-gray-500">
            Marge = CA HT − coût d'achat des lots vendus − démarque. La production maison sort à coût nul (son coût est dans les charges de la compta) ;
            un lot acheté sans prix d'achat connu est exclu du coût et signalé par la colonne « Coût connu » (caisse et B2B).
            La consommation personnelle, les échantillons, les repas du personnel et le sponsoring ne sont pas de la démarque : ils sont dans « Conso perso », au coût d'achat.
            {vueLignes === 'semaine' && " Le pourcentage à côté du CA compare à la semaine précédente ; la semaine en cours n'est pas comparée tant qu'elle n'est pas finie."}
          </p>
        </div>
      )}

      {mois && mois.some((m) => m.detail.eshop_clients) && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <h2 className="px-3 pt-3 text-lg font-semibold text-gray-900">Boutique en ligne et épicerie</h2>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-2">{vueLignes === 'mois' ? 'Mois' : 'Semaine'}</th>
                <th className="px-3 py-2 text-right" title="CA HT en ligne des clients notés « Nouveau client » à la préparation">Nouveaux clients</th>
                <th className="px-3 py-2 text-right">Clients du magasin</th>
                <th className="px-3 py-2 text-right" title="Clients pas encore notés : à dire sur l'écran de préparation ou la fiche client">Pas encore dit</th>
                <th className="px-3 py-2 text-right" title="Part de l'épicerie sèche (épiceries, boissons, apéritifs, fruits secs, petit-déjeuner, hygiène) dans les paniers en ligne, hors consignes">Épicerie dans les paniers</th>
                <th className="px-3 py-2 text-right" title="Stock d'épicerie sèche au prix d'achat HT, en fin de période">Stock épicerie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 tabular-nums">
              {mois.map((m) => {
                const c = m.detail.eshop_clients;
                return (
                  <tr key={m.month}>
                    <td className={`px-3 py-2 ${vueLignes === 'mois' ? 'capitalize' : ''}`}>
                      {vueLignes === 'semaine' && m.du && m.au ? `${jourCourt(m.du)} → ${jourCourt(m.au)}` : libelleMois(m.month)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c ? eur(c.nouveaux_ht) : '—'}
                      {c && c.nouveaux_nombre > 0 && <span className="ml-1 text-gray-500">({c.nouveaux_nombre})</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{c ? eur(c.magasin_ht) : '—'}</td>
                    <td className={`px-3 py-2 text-right ${c && c.non_renseigne_ht > 0 ? 'text-amber-700' : ''}`}>{c ? eur(c.non_renseigne_ht) : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {m.detail.part_epicerie_eshop === null || m.detail.part_epicerie_eshop === undefined ? '—' : `${Math.round(m.detail.part_epicerie_eshop * 100)} %`}
                    </td>
                    <td className="px-3 py-2 text-right">{m.detail.stock_epicerie === undefined ? '—' : eur(m.detail.stock_epicerie)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-gray-500">
            Nouveau client ou client du magasin : c'est vous qui le dites, à la préparation de sa première commande en ligne (ou sur sa fiche client) ; rien ne
            permet de le deviner. Ce qui n'est pas encore dit reste dans « Pas encore dit », jamais réparti au hasard. Le stock d'épicerie est reconstitué à la fin
            de chaque période à partir des mouvements de stock, au prix d'achat des lots.
          </p>
        </div>
      )}
    </div>
  );
}
